import { api } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import { useQueries, useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import type { LearningTopicDoc, LearningTopicStatsResponse } from "@a7y/types";

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export function AdoptionMetricsTab() {
  const { data: topicsData, isLoading: isTopicsLoading } = useQuery({
    queryKey: ["learning-topics"],
    queryFn: () => api.getLearningTopics(),
  });

  const topics: LearningTopicDoc[] = topicsData ?? [];

  const statsQueries = useQueries({
    queries: topics.map((topic) => ({
      queryKey: ["learning-topic-stats", topic.id],
      queryFn: () => api.getLearningTopicStats(topic.id),
      enabled: topics.length > 0,
    })),
  });

  const isLoading = isTopicsLoading || statsQueries.some((q) => q.isLoading);

  const rows: { topic: LearningTopicDoc; stats: LearningTopicStatsResponse | null }[] =
    topics.map((topic, i) => ({
      topic,
      stats: statsQueries[i]?.data ?? null,
    }));

  return (
    <Table dense striped>
      <TableHead>
        <TableRow>
          <TableHeader>Topic</TableHeader>
          <TableHeader>Total Sends</TableHeader>
          <TableHeader>7-Day Adoption Rate</TableHeader>
          <TableHeader>14-Day Adoption Rate</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={4} className="text-center">
              <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
            </TableCell>
          </TableRow>
        ) : rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={4} className="py-8 text-center">
              <BarChart3 className="mx-auto h-12 w-12 text-zinc-400" />
              <p className="mt-2 text-zinc-500">
                No topics to show metrics for
              </p>
            </TableCell>
          </TableRow>
        ) : (
          rows.map(({ topic, stats }) => (
            <TableRow key={topic.id}>
              <TableCell className="font-medium">{topic.title}</TableCell>
              <TableCell>{stats?.totalSent ?? "--"}</TableCell>
              <TableCell>
                {stats != null && stats.totalSent > 0
                  ? formatPercent(stats.adoptedWithin7d / stats.totalSent)
                  : "--"}
              </TableCell>
              <TableCell>
                {stats != null && stats.totalSent > 0
                  ? formatPercent(stats.adoptedWithin14d / stats.totalSent)
                  : "--"}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
